import { useRef, useState, useEffect } from 'react';
import { Helmet } from 'react-helmet';
import { Outlet, useParams } from 'react-router-dom';
import { useQuery } from '@apollo/client';
import { useTheme } from 'styled-components';
import Loader from '../components/Loader';
import {
  Section,
  SectionItem,
  SectionLink,
  SectionName,
} from '../components/Chrome';
import {
  Drawer,
  DrawerExit,
  DrawerLink,
  DrawerList,
  DrawerTrigger,
} from '../components/Drawer';
import DeleteTopicModal from '../modals/DeleteTopic';
import UnarchiveTopicModal from '../modals/UnarchiveTopic';
import Icon from '../components/Icon';
import { ARCHIVED_TOPICS } from '../utilities/queries';
import { getRelativeTime } from '../utilities/getRelativeTime';

const SpaceSettingsArchivesPage = () => {
  const params = useParams();
  const theme = useTheme();
  const [fetchMoreEnabled, setFetchMoreEnabled] = useState(true);

  const limit = Math.ceil((document.documentElement.clientHeight - 56) / 40);

  const {
    data: dataArchived,
    networkStatus,
    fetchMore,
  } = useQuery(ARCHIVED_TOPICS, {
    variables: { slug: params.s, limit },
    notifyOnNetworkStatusChange: true,
    onCompleted: (data) => {
      if (
        data.space.topics.length === 0 ||
        data.space.topics.length !== limit
      ) {
        setFetchMoreEnabled(false);
      } else {
        setFetchMoreEnabled(true);
      }
    },
  });

  const [isDeleteTopicModalOpen, setIsDeleteTopicModalOpen] = useState(false);
  const [isUnarchiveTopicModalOpen, setIsUnarchiveTopicModalOpen] =
    useState(false);

  // Used to pass user data to action modals
  const [topic, setTopic] = useState();

  // Tracks the space user the viewer is interacting with
  const listRef = useRef();
  const [trackTopic, setTrackTopic] = useState();

  useEffect(() => {
    const handleScroll = () => {
      const doc = document.documentElement;

      if (
        fetchMoreEnabled &&
        networkStatus !== 3 &&
        doc.scrollTop + doc.clientHeight >= doc.scrollHeight
      ) {
        fetchMore({
          variables: {
            after:
              dataArchived &&
              dataArchived.space.topics[dataArchived.space.topics.length - 1]
                .id,
          },
          updateQuery: (previousResult, { fetchMoreResult }) => {
            return {
              space: {
                ...previousResult.space,
                topics: [
                  ...previousResult.space.topics,
                  ...fetchMoreResult.space.topics,
                ],
              },
            };
          },
        });
      }
    };

    addEventListener('scroll', handleScroll);

    return () => {
      removeEventListener('scroll', handleScroll);
    };
  });

  return (
    <>
      <Section>
        {!dataArchived ? (
          <Loader />
        ) : (
          <>
            <Helmet>
              <title>Archives</title>
            </Helmet>
            <SectionName isSecondLevel>Archives</SectionName>
            {dataArchived?.space?.topics.length !== 0 ? (
              dataArchived.space.topics?.map((topic) => (
                <Drawer key={topic.id} onClose={() => setTrackTopic(null)}>
                  <DrawerTrigger onClick={() => setTrackTopic(topic.id)}>
                    <SectionLink as="button" isActive={trackTopic === topic.id}>
                      <span>{topic.name}</span>
                      <span
                        style={{ display: 'flex', gridGap: theme.space[3] }}
                      >
                        {topic.archivedAt &&
                          `(${getRelativeTime(topic.archivedAt)})`}{' '}
                        <Icon name="more" />
                      </span>
                    </SectionLink>
                  </DrawerTrigger>
                  <DrawerList ref={listRef}>
                    <DrawerExit />
                    <DrawerLink
                      to={`/s/${params.s}/t/${topic.publicId}`}
                      state={{ fromArchives: true }}
                    >
                      View topic
                    </DrawerLink>
                    <DrawerLink
                      as="button"
                      onClick={() => {
                        setTopic(topic);
                        setIsDeleteTopicModalOpen(true);
                      }}
                      dontRunOnClose
                    >
                      Delete topic
                    </DrawerLink>
                    <DrawerLink
                      as="button"
                      onClick={() => {
                        setTopic(topic);
                        setIsUnarchiveTopicModalOpen(true);
                      }}
                      dontRunOnClose
                    >
                      Unarchive topic
                    </DrawerLink>
                  </DrawerList>
                </Drawer>
              ))
            ) : (
              <SectionItem>
                There are no archived topics in this space — yet.
              </SectionItem>
            )}
            {networkStatus === 3 && (
              <SectionItem>
                <Loader />
              </SectionItem>
            )}
          </>
        )}
      </Section>
      {isDeleteTopicModalOpen && (
        <DeleteTopicModal
          topicId={topic.id}
          spaceSlug={params.s}
          onClose={() => {
            setTrackTopic(null);
            setIsDeleteTopicModalOpen(false);
          }}
          dontRedirect
        />
      )}
      {isUnarchiveTopicModalOpen && (
        <UnarchiveTopicModal
          topicId={topic.id}
          spaceSlug={params.s}
          onClose={() => {
            setTrackTopic(null);
            setIsUnarchiveTopicModalOpen(false);
          }}
        />
      )}
      <Outlet />
    </>
  );
};

export default SpaceSettingsArchivesPage;
